<template>
	<div class="flow-picto-wrapper" :shape="shape" :animate="animate" :type="type" :state="state">
		<div
			class="flow-picto-container"
			:size="size"
			:state="state"
			:borderState="borderState"
			:type="type"
			:effects="effects"
			:shape="shape"
			:style="styleObj"
			:loading="loading"
			@click="handleClick($event)"
		>
			<span class="flow-picto-content">
				<slot>
					{{ computedLabel }}
				</slot>
			</span>

			<svg v-if="shape === 'squircle'" width="0" height="0">
				<defs>
					<clipPath id="squircle" clipPathUnits="objectBoundingBox">
						<path d="M .5,0 C .1,0 0,.1 0,.5 0,.9 .1,1 .5,1 .9,1 1,.9 1,.5 1,.1 .9,0 .5,0 Z" />
					</clipPath>
				</defs>
			</svg>
		</div>
		<div v-if="$slots['title']" class="flow-picto-title">
			<slot name="title" />
		</div>
		<div v-if="$slots['number']" class="flow-picto-number">
			<slot name="number" />
		</div>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";

const SIZE = [
	"xx-small",
	"x-small",
	"small",
	"medium",
	"large",
	"x-large",
	"xx-large",
	"s",
	"m",
	"l",
	"xl"
] as const;

const STATE = [
	"default",
	"primary",
	"primary-light",
	"primary-dark",
	"success",
	"error",
	"error-light",
	"error-dark",
	"warning",
	"warning-light",
	"warning-dark",
	"success-light",
	"success-dark",
	"light",
	"dark",
	"border",
	"element",
	"avatar"
] as const;

const BORDER_STATE = ["default", "primary", "success", "error", "warning", "secondary"] as const;

const TYPE = ["solid", "transparent"] as const;

const SHAPE = ["hexagon", "default", "squircle", "circle"] as const;

export type PictogramType = typeof TYPE[number];
export type PictogramState = typeof STATE[number];
export type PictogramBorderState = typeof BORDER_STATE[number];
export type PictogramSize = typeof SIZE[number];
export type PictogramShape = typeof SHAPE[number];

/**
 * Pictogram is a visual representation of a content like label, header or objects.
 * Pictograms are illustrative and expressive. They simply complex ideas and aid to clearly communicate the content. Emoticons, icons, logos, illustrations can be represented using pictograms.
 */
export default defineComponent({
	name: "Pictogram",

	props: {
		/**
		 * 'xx-small' | 'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' | 's' | 'm' | 'l' | 'xl'
		 */
		size: {
			type: String as PropType<PictogramSize>,
			default: "small"
			// validator(value: PictogramSize) {
			// 	return SIZE.includes(value);
			// }
		},

		/**
		 * 	'default' | 'primary' | 'primary-light' | 'primary-dark' |'success' | 'error' | 'error-light' | 'error-dark' | 'warning' | 'warning-light' | 'warning-dark' | 'success-light' | 'success-dark' | 'light' | 'dark' | 'border' | 'element' | 'avatar'
		 */
		state: {
			type: String as PropType<PictogramState>,
			default: "default"
			// validator(value: PictogramState) {
			// 	return STATE.includes(value);
			// }
		},

		/**
		 * 	'default' | 'primary' | 'success' | 'error' |'warning' | 'secondary'
		 */
		borderState: {
			type: String as PropType<PictogramBorderState>,
			default: "default"
			// validator(value: PictogramBorderState) {
			// 	return BORDER_STATE.includes(value);
			// }
		},

		/**
		 * 'solid' | 'transparent'
		 */
		type: {
			type: String as PropType<PictogramType>,
			default: "solid"
			// validator(value: PictogramType) {
			// 	return TYPE.includes(value);
			// }
		},

		/**
		 * true | false <br/> Used to enable/disable :hover, :active, :focus effects <br/> Default : true
		 */
		effects: {
			type: Boolean,
			default: false
		},

		/**
		 * 'hexagon' | 'default' |  'squircle' | 'circle'
		 */
		shape: {
			type: String as PropType<PictogramShape>,
			default: "default"
			// validator(value: PictogramShape) {
			// 	return SHAPE.includes(value);
			// }
		},

		/**
		 * label display in Pictogram if state==='avatar' it should be max 2 characters
		 */
		label: {
			type: String,
			default: null
		},

		/**
		 * Used while border state is used
		 */
		loading: {
			type: Boolean,
			default: false
		},

		/**
		 * Used for user attention to click or indicating some info
		 */
		animate: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			colors: [] as string[],
			sizes: {
				"xx-small": 16,
				"x-small": 24,
				small: 36,
				medium: 48,
				large: 60,
				"x-large": 72,
				"xx-large": 80,
				s: 20,
				m: 28,
				l: 36,
				xl: 44
			}
		};
	},

	computed: {
		computedLabel() {
			const label: string | null = this.label;

			if (label && label.length > 2) {
				return label.substring(0, 2);
			}

			return label;
		},
		background() {
			return this.hashCode(this.computedLabel);
		},
		styleObj() {
			if (this.state === "avatar") {
				return {
					backgroundColor: this.background,
					fontSize: `${this.sizes[this.size] / 2.5}px`,
					lineHeight: `${this.sizes[this.size] / 3}px`,
					fontWeight: "bold",
					color: "var(--dashboard-background)"
				};
			} else {
				return {};
			}
		}
	},
	mounted() {
		this.colors = this.generateHslaColors(50, 60, 1.0, 10);
	},
	methods: {
		handleClick(e: MouseEvent) {
			/**
			 * Click event
			 *
			 * @event click
			 */
			this.$emit("click", e);
		},

		hashCode(str: string | null) {
			if (!str) {
				return this.colors[0];
			}

			let sum = 0;

			for (const substr of str) {
				sum += substr.charCodeAt(0);
			}

			return this.colors[sum % this.colors.length];
		},

		generateHslaColors(saturation: number, lightness: number, alpha: number, amount: number) {
			const colors = [];
			const huedelta = Math.trunc(360 / amount);

			for (let i = 0; i < amount; i++) {
				const hue = i * huedelta;
				colors.push(`hsla(${hue},${saturation}%,${lightness}%,${alpha})`);
			}

			return colors;
		}
	}
});
</script>
<style lang="scss">
$hexagon-clip-path: polygon(
	98.66025% 45%,
	99.39693% 46.5798%,
	99.84808% 48.26352%,
	100% 50%,
	99.84808% 51.73648%,
	99.39693% 53.4202%,
	98.66025% 55%,
	78.66025% 89.64102%,
	77.66044% 91.06889%,
	76.42788% 92.30146%,
	75% 93.30127%,
	73.4202% 94.03794%,
	71.73648% 94.48909%,
	70% 94.64102%,
	30% 94.64102%,
	28.26352% 94.48909%,
	26.5798% 94.03794%,
	25% 93.30127%,
	23.57212% 92.30146%,
	22.33956% 91.06889%,
	21.33975% 89.64102%,
	1.33975% 55%,
	0.60307% 53.4202%,
	0.15192% 51.73648%,
	0% 50%,
	0.15192% 48.26352%,
	0.60307% 46.5798%,
	1.33975% 45%,
	21.33975% 10.35898%,
	22.33956% 8.93111%,
	23.57212% 7.69854%,
	25% 6.69873%,
	26.5798% 5.96206%,
	28.26352% 5.51091%,
	30% 5.35898%,
	70% 5.35898%,
	71.73648% 5.51091%,
	73.4202% 5.96206%,
	75% 6.69873%,
	76.42788% 7.69854%,
	77.66044% 8.93111%,
	78.66025% 10.35898%
);
@function str-replace($string, $search, $replace: "") {
	$index: str-index($string, $search);

	@if $index {
		@return str-slice($string, 1, $index - 1) + $replace +
			str-replace(str-slice($string, $index + str-length($search)), $search, $replace);
	}

	@return $string;
}
@mixin pictogram($background-colors, $border-states, $loading-gradients) {
	.flow-picto-wrapper {
		@each $state, $color in $background-colors {
			&[state="#{$state}"][type="solid"][animate] {
				&:before {
					background-color: $color;
				}
			}
		}
	}
	.flow-picto-container {
		@each $state, $color in $background-colors {
			&[state="#{$state}"][type="solid"] {
				background-color: $color;
				&:after {
					background-color: $color;
				}
			}
		}
		@each $state, $color in $border-states {
			@if $state != "default" {
				&[borderState="#{$state}"]:not([loading]) {
					&:before {
						content: "";
						background: $color;
					}
					&:after {
						content: "";
					}
				}
			}
		}
		@each $state, $color in $loading-gradients {
			@if $state != "default" {
				&[borderState="#{$state}"][loading] {
					&:before {
						content: "";
						background: $color;
					}
					&:after {
						content: "";
					}
				}
			}
		}

		&[type="transparent"] {
			background-color: transparent;
		}
	}
}

$sizes: (
	"xx-small": 16px,
	"x-small": 24px,
	"small": 36px,
	"medium": 48px,
	"large": 60px,
	"x-large": 72px,
	"xx-large": 80px,
	"s": 20px,
	"m": 28px,
	"l": 36px,
	"xl": 44px
);

$font-sizes: (
	"xx-small": 12px,
	"x-small": 16px,
	"small": 24px,
	"medium": 32px,
	"large": 40px,
	"x-large": 48px,
	"xx-large": 54px,
	"s": 10px,
	"m": 14px,
	"l": 16px,
	"xl": 24px
);

$border-radius: (
	"xx-small": 2px,
	"x-small": 2px,
	"small": 4px,
	"medium": 4px,
	"large": 4px,
	"x-large": 4px,
	"xx-large": 4px,
	"s": 4px,
	"m": 4px,
	"l": 4px,
	"xl": 4px
);

.flow-picto-wrapper {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	cursor: default;
	flex-shrink: 0;
	outline: none;
	position: relative;
	overflow: visible;

	> div.flow-picto-title {
		position: absolute;
		bottom: -24px;
		white-space: nowrap;
	}
	> div.flow-picto-number {
		position: absolute;
		top: -4px;
		right: -4px;

		display: inline-flex;
		align-items: center;
		justify-content: center;

		height: 16px;
		width: 16px;
		border-radius: 8px;
		text-transform: uppercase;
		color: white;
		font-size: 8px;
		background-color: var(--dashboard-primary);
		font-weight: bold;
		z-index: 3;
	}

	&[animate] {
		overflow: visible;

		&:before {
			position: absolute;
			width: 100%;
			height: 100%;
			transform-origin: 50% 50%;
		}
		&:before {
			content: "";
			animation: picto-pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
		}

		&[shape="hexagon"] {
			&:before {
				clip-path: $hexagon-clip-path;
			}
		}
		&[shape="squircle"] {
			&:before {
				clip-path: url(#squircle);
			}
		}
		&[shape="circle"] {
			&:before {
				clip-path: circle(50%);
			}
		}
		&[shape="default"] {
			&:before {
				clip-path: inset(0px 0px 0px round 4px);
			}
		}
	}
	@keyframes picto-pulse-ring {
		0% {
			transform: scale(1.2);
		}
		80%,
		100% {
			opacity: 0;
			transform: scale(1.6);
		}
	}
}
.flow-picto-container {
	display: inline-flex;
	justify-content: center;
	align-items: center;
	cursor: default;
	flex-shrink: 0;
	outline: none;
	position: relative;
	overflow: hidden;

	.flow-picto-content {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: inherit;
		width: inherit;
	}

	img {
		height: inherit;
		width: inherit;
	}
	@each $name, $size in $sizes {
		&[size="#{$name}"] {
			width: $size;
			min-width: $size;
			height: $size;
			border-radius: map-get($border-radius, $name);
			font-size: map-get($font-sizes, $name);
		}
	}

	&[effects] {
		cursor: pointer;
		&:active {
			filter: contrast(80%);
		}

		&:hover:not(:active) {
			filter: contrast(75%);
		}
	}

	> *:not(.picto-shape) {
		z-index: 2;
	}

	&[shape="hexagon"] {
		clip-path: $hexagon-clip-path;
		&:after {
			clip-path: $hexagon-clip-path;
		}
	}
	&[shape="squircle"] {
		clip-path: url(#squircle);
		&:after {
			clip-path: url(#squircle);
		}
	}
	&[shape="circle"] {
		clip-path: circle(50%);
		&:after {
			clip-path: circle(50%);
		}
	}
	&[shape="default"] {
		clip-path: inset(0px 0px 0px round 4px);
		&:after {
			clip-path: inset(0px 0px 0px round 4px);
		}
	}

	&:before {
		position: absolute;
		width: 100%;
		height: 100%;
		transform-origin: 50% 50%;
		transform: rotate(0deg) scale(1.4);
		animation: 3s rotate linear infinite;
	}
	&:after {
		position: absolute;
		width: 100%;
		height: 100%;
		transform: scale(0.9);
		transform-origin: 50% 50%;
		pointer-events: none;
	}

	@keyframes rotate {
		to {
			transform: rotate(360deg) scale(1.4);
		}
	}
}

@include pictogram(
	(
		"default": var(--dashboard-icon-background),
		"primary": var(--dashboard-primary),
		"primary-light": var(--dashboard-primary-light),
		"primary-dark": var(--dashboard-primary-dark),
		"success": var(--dashboard-success),
		"success-light": var(--dashboard-success-light),
		"success-dark": var(--dashboard-success-dark),
		"error": var(--dashboard-danger),
		"error-light": var(--dashboard-danger-light),
		"error-dark": var(--dashboard-danger-dark),
		"warning": var(--dashboard-warning),
		"warning-light": var(--dashboard-warning-light),
		"warning-dark": var(--dashboard-warning-dark),
		"light": var(--dashboard-element-light),
		"dark": var(--dashboard-element-dark),
		"element": var(--dashboard-element),
		"border": var(--dashboard-border-light)
	),
	(
		"default": transparent,
		"primary": var(--dashboard-primary),
		"success": var(--dashboard-success),
		"error": var(--dashboard-danger),
		"warning": var(--dashboard-warning),
		"secondary": var(--dashboard-secondary)
	),
	(
		"default": transparent,
		"primary":
			linear-gradient(
				var(--primary-500),
				var(--primary-400),
				var(--primary-300),
				var(--primary-200),
				var(--primary-100),
				#fff
			),
		"success":
			linear-gradient(
				var(--success-500),
				var(--success-400),
				var(--success-300),
				var(--success-200),
				var(--success-100),
				#fff
			),
		"error":
			linear-gradient(
				var(--danger-500),
				var(--danger-400),
				var(--danger-300),
				var(--danger-200),
				var(--danger-100),
				#fff
			),
		"warning":
			linear-gradient(
				var(--warning-500),
				var(--warning-400),
				var(--warning-300),
				var(--warning-200),
				var(--warning-100),
				#fff
			),
		"secondary":
			linear-gradient(
				var(--secondary-500),
				var(--secondary-400),
				var(--secondary-300),
				var(--secondary-200),
				var(--secondary-100),
				#fff
			)
	)
);

div.sideout-container {
	@include pictogram(
		(
			"default": var(--slideout-icon-background),
			"primary": var(--slideout-primary),
			"primary-light": var(--slideout-primary-light),
			"primary-dark": var(--slideout-primary-dark),
			"success": var(--slideout-success),
			"success-light": var(--slideout-success-light),
			"success-dark": var(--slideout-success-dark),
			"error": var(--slideout-danger),
			"error-light": var(--slideout-danger-light),
			"error-dark": var(--slideout-danger-dark),
			"warning": var(--slideout-warning),
			"warning-light": var(--slideout-warning-light),
			"warning-dark": var(--slideout-warning-dark),
			"light": var(--slideout-element-light),
			"dark": var(--slideout-element-dark),
			"element": var(--slideout-element),
			"border": var(--slideout-border-light)
		),
		(
			"default": transparent,
			"primary": var(--slideout-primary),
			"success": var(--slideout-success),
			"error": var(--slideout-danger),
			"warning": var(--slideout-warning),
			"secondary": var(--slideout-secondary)
		),
		(
			"default": transparent,
			"primary":
				linear-gradient(
					var(--primary-500),
					var(--primary-400),
					var(--primary-300),
					var(--primary-200),
					var(--primary-100),
					#fff
				),
			"success":
				linear-gradient(
					var(--success-500),
					var(--success-400),
					var(--success-300),
					var(--success-200),
					var(--success-100),
					#fff
				),
			"error":
				linear-gradient(
					var(--danger-500),
					var(--danger-400),
					var(--danger-300),
					var(--danger-200),
					var(--danger-100),
					#fff
				),
			"warning":
				linear-gradient(
					var(--warning-500),
					var(--warning-400),
					var(--warning-300),
					var(--warning-200),
					var(--warning-100),
					#fff
				),
			"secondary":
				linear-gradient(
					var(--secondary-500),
					var(--secondary-400),
					var(--secondary-300),
					var(--secondary-200),
					var(--secondary-100),
					#fff
				)
		)
	);
}
</style>
